import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import type { Role } from '@foundry/shared';
import { ApiError, apiFetch } from './client.js';
import type { User } from './types.js';

/** The current principal, or `null` when unauthenticated. Never throws on 401. */
export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: async (): Promise<User | null> => {
      try {
        return (await apiFetch<{ user: User }>('/auth/me')).user;
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 30_000,
  });
}

interface Credentials {
  email: string;
  password: string;
  /** TOTP code, sent on the retry after an `mfa_required` response. */
  code?: string;
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Credentials) =>
      apiFetch<{ user: User }>('/auth/login', { method: 'POST', body }),
    onSuccess: ({ user }) => qc.setQueryData(['me'], user),
  });
}

export function useMfaSetup() {
  return useMutation({
    mutationFn: () =>
      apiFetch<{ secret: string; otpauthUrl: string }>('/auth/mfa/setup', { method: 'POST' }),
  });
}

export function useMfaEnable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      apiFetch<{ ok: true }>('/auth/mfa/enable', { method: 'POST', body: { code } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

export function useMfaDisable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      apiFetch<{ ok: true }>('/auth/mfa/disable', { method: 'POST', body: { code } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

export interface InvitationSummary {
  email: string;
  role: Role;
}

export interface PendingInvitation {
  id: string;
  email: string;
  role: Role;
  expiresAt: string;
  createdAt: string;
}

/** Resolves an invite token to the email/role it is for, or null if invalid. */
export function useInviteLookup(token: string) {
  return useQuery({
    queryKey: ['invite', token],
    queryFn: async (): Promise<InvitationSummary | null> => {
      try {
        return (
          await apiFetch<{ invitation: InvitationSummary }>(
            `/auth/invite?token=${encodeURIComponent(token)}`,
          )
        ).invitation;
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
    enabled: token.length > 0,
    retry: false,
  });
}

export function useAcceptInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      token: string;
      password: string;
      firstName: string;
      lastName: string;
      projectName?: string;
    }) => apiFetch<{ user: User }>('/auth/accept-invitation', { method: 'POST', body }),
    onSuccess: ({ user }) => qc.setQueryData(['me'], user),
  });
}

/** Pending invitations (super admin). */
export function useInvitations() {
  return useQuery({
    queryKey: ['invitations'],
    queryFn: () =>
      apiFetch<{ invitations: PendingInvitation[] }>('/auth/invitations').then(
        (r) => r.invitations,
      ),
  });
}

interface CreatedInvitation {
  invitation: { id: string; email: string; role: Role; expiresAt: string };
  /** The single-use token for the invite link. */
  token: string;
}

export function useCreateInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; role: 'founder' | 'developer' }) =>
      apiFetch<CreatedInvitation>('/auth/invitations', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invitations'] }),
  });
}

export function useRevokeInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ ok: true }>(`/auth/invitations/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invitations'] }),
  });
}

/** Rotates a pending invitation's token and returns a fresh link token. */
export function useRegenerateInvitationLink() {
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ token: string }>(`/auth/invitations/${id}/link`, { method: 'POST' }).then(
        (r) => r.token,
      ),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<{ ok: true }>('/auth/logout', { method: 'POST' }),
    onSuccess: async () => {
      qc.setQueryData(['me'], null);
      // Drop every cached project/build so the next user starts clean.
      await qc.invalidateQueries();
    },
  });
}

export function useChangePassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) =>
      apiFetch<{ ok: true }>('/auth/change-password', { method: 'POST', body }),
    // The change clears mustChangePassword server-side; refetch to pick that up.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

export function useChangeEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { newEmail: string; password: string }) =>
      apiFetch<{ user: User }>('/auth/change-email', { method: 'POST', body }),
    onSuccess: ({ user }) => qc.setQueryData(['me'], user),
  });
}
